import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError, type SaleResource, SalesController_readByNumber_v1 } from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconReceipt, IconReceiptOff, IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { isNotFound, messageForFailure } from '@/api/failure';
import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { salePath } from '../paths';
import { labelForPaymentMode } from './payment-modes';
import {
  namesOneTill,
  RECEIPT_NUMBER_EXAMPLE,
  RECEIPT_NUMBER_MALFORMED,
  receiptNumberSchema,
} from './receipt-number';

/**
 * The number off a customer's receipt, and the sale it belongs to.
 *
 * ## This is the counter path, and it is the point of the screen it sits on
 *
 * A customer comes back with a printed receipt and a question about a charge. The
 * number on that paper is all anybody has — not an id, not a day, not which till
 * — so this is a single field that takes what is in their hand and answers with
 * the record. It is put where a manager already is, at the top of the sales
 * screen and again on a sale's own record, rather than behind a search page
 * somebody has to navigate to first.
 *
 * ## The trap: a receipt number is not a key, and this screen must not pretend it is
 *
 * **The counter is the terminal's own** (R-402). `sales_terminal_kind_number_key`
 * is unique on `(terminal_id, sequence_kind, number_value)` and on nothing
 * narrower, so till LK and till GT each print a `482` for two different amounts,
 * and in a park with eleven tills a bare `482` names up to eleven sales. `GET
 * /v1/sales/by-number/{number}` therefore answers with a **page** rather than a
 * sale — `SalesController.readByNumber` publishes the ambiguity rather than
 * hiding it behind a first row — and this component is the other half of that
 * decision.
 *
 * So there are two answers to a found lookup, not one:
 *
 * - **One sale** — navigate to its record. Which is what the whole printed form
 *   gives you: the prefix resolves the till, `sales_terminal_kind_number_key`
 *   serves it, and at most one sale can come back.
 * - **Several** — render them and let the person choose. Each candidate leads
 *   with its **full** `receiptNumber`, because the prefix is the disambiguator
 *   and it is printed on the paper in their hand.
 *
 * **Nothing here picks the first row**, and that is the whole discipline of the
 * component. `items[0]` on a multi-row answer typechecks perfectly, renders
 * perfectly, and is the wrong sale — a customer told about somebody else's basket
 * for somebody else's money. It is the single most likely defect in this ticket
 * and there is no test on this branch today, so it is worth reading for.
 *
 * The other shape that was rejected: a till picker beside the field. It would
 * make every ordinary lookup — somebody typing the whole number off an intact
 * receipt — into two controls where one would do, and the device directory it
 * would need is `OWNER`-only, so a manager would get an empty picker and no way
 * to disambiguate at all. The prefix already carries the answer; the collection
 * covers the case where the paper does not.
 *
 * ## What "not found" is allowed to say
 *
 * **A number at an outlet outside the caller's reach answers `404` byte for byte
 * identically to one nobody has printed** (FR-437, FR-128), deliberately, because
 * a refusal that told them apart is an enumeration oracle — the same rule as
 * `AUTH_CREDENTIALS_INVALID` covering an unknown staff code, a wrong password and
 * a deactivated user, and `docs/ui-conventions.md` names it under "Preserve the
 * non-distinctions". So the copy below says *no receipt with that number* and
 * nothing more. It does not hedge, it does not offer "you may not have access to
 * it", and **there is no branch here that could tell the two apart** if somebody
 * asked it to — the only thing the empty answer varies on is whether what was
 * typed carried a prefix, which is a fact about the input and not about the
 * server's reason.
 *
 * What it does say is that a **refunded** sale would have been found — because
 * the most dangerous misreading of an empty answer, on the one screen where
 * somebody is asking about a charge that may have been given back, is "it must
 * have been refunded".
 */

const lookupSchema = z.object({ number: receiptNumberSchema });

/**
 * Every state the panel can be in. One value, so two cannot coexist.
 *
 * There is no `found`: the answer to a single sale is a navigation, and by the
 * time it would be written this component is on its way out.
 *
 * `number` on `missing` and on `several` is what the **server was asked**, kept so
 * the sentence names it even after somebody starts correcting a digit in the
 * field above.
 */
type Answer =
  | { readonly kind: 'idle' }
  | { readonly kind: 'looking' }
  /** Found, and it is the record already on screen — so there is nowhere to go. */
  | { readonly kind: 'here'; readonly number: string }
  | {
      readonly kind: 'several';
      readonly number: string;
      readonly sales: readonly SaleResource[];
      /** The server's `total`. Equal to `sales.length` in every documented case — see below. */
      readonly total: number;
    }
  | { readonly kind: 'missing'; readonly number: string; readonly hadPrefix: boolean }
  | { readonly kind: 'failed'; readonly error: unknown };

export function ReceiptLookup() {
  const client = useApiClient();
  const navigate = useNavigate();

  // Only ever compared against a path this file built with `salePath`, so both
  // sides are encoded the same way and neither is parsed.
  const location = useLocation();

  const form = useForm({
    resolver: zodResolver(lookupSchema),
    mode: 'onTouched',
    defaultValues: { number: '' },
  });

  const [answer, setAnswer] = useState<Answer>({ kind: 'idle' });

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: z.infer<typeof lookupSchema>) => {
    // What was typed, trimmed by the schema and otherwise untouched. Not
    // uppercased and not stripped of its leading zeros: the API uppercases the
    // prefix itself and reads padding as presentation, so `lk-482` and
    // `LK-000482` are the same lookup — and the value that is sent is the value
    // somebody can see they typed.
    const { number } = values;

    setAnswer({ kind: 'looking' });

    try {
      const page = await client.request(SalesController_readByNumber_v1, {
        path: { number },
      });

      const sales = page.items;

      // **One sale, and only then a navigation.** `sales[0]` is read here and
      // nowhere else, guarded by a length of exactly one, so the "first row of
      // several" defect has no site to occur at. `noUncheckedIndexedAccess`
      // makes the `undefined` branch explicit rather than optional; a page whose
      // `items` disagreed with its own length falls through to the collection
      // below, which is the safe direction.
      const only = sales.length === 1 ? sales[0] : undefined;

      if (only !== undefined) {
        const target = salePath(only.id);

        // **The one found case that is not a navigation.** This panel sits on a
        // sale's own record as well as on the list, so the receipt already on
        // screen resolves to the URL that is already current — `navigate` would be
        // a no-op, this component would stay mounted, and the panel would read
        // "Looking…" under a record that is in fact the answer. Said plainly
        // instead: the question was asked and answered, and pressing Find again
        // must never look like a control that does nothing.
        if (target === location.pathname) {
          setAnswer({ kind: 'here', number: only.receiptNumber });

          return;
        }

        // `replace: false`: the sales list stays behind this in the history, so
        // Back returns to the day somebody was reading rather than to an empty
        // field.
        await navigate(target);

        // Cleared so the next screen's panel starts empty. A no-op where the
        // navigation unmounted this, which is the ordinary path.
        setAnswer({ kind: 'idle' });

        return;
      }

      // Documented not to happen — `readByNumber` answers `404` rather than an
      // empty page, because "no such receipt" is a miss and not a list with
      // nothing in it. Handled anyway and folded into the same answer the `404`
      // produces, so the two cannot ever be worded differently on screen.
      if (sales.length === 0) {
        setAnswer({ kind: 'missing', number, hadPrefix: namesOneTill(number) });

        return;
      }

      setAnswer({ kind: 'several', number, sales, total: page.total });
    } catch (error: unknown) {
      // Branched on the code and never on the status. A 404 here is an *answer*
      // to the question rather than a failure to answer it, and
      // `messageForFailure` would word it as "that record no longer exists, go
      // back and refresh the list" — which is about a row that was deleted and is
      // not what happened.
      if (isNotFound(error)) {
        setAnswer({ kind: 'missing', number, hadPrefix: namesOneTill(number) });

        return;
      }

      // `VALIDATION_FAILED` means the server read the shape differently from the
      // field, which the rules in `receipt-number.ts` are written to prevent —
      // they are `parseSaleNumber` restated. If it happens anyway the two have
      // drifted, and the honest place for the message is under the field that
      // produced it rather than in a panel about the lookup. Said in this app's
      // own words and not the server's prose: `docs/ui-conventions.md` is
      // explicit that a refusal is worded from its `code` and only from its
      // `code`.
      if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
        form.setError('number', { message: RECEIPT_NUMBER_MALFORMED });
        setAnswer({ kind: 'idle' });

        return;
      }

      setAnswer({ kind: 'failed', error });
    }
  };

  return (
    <section
      aria-label="Find a sale by its printed receipt number"
      // A band of the field, not a card on it. The panel is the surface now, so
      // the bordered, rounded enamel box this used to draw was a card inside a
      // card — and with the field uncapped the one input in it stretched the
      // width of the panel. What is left is what a band is: the head's own
      // gutter, a measure for the field, and a rule between this and the records
      // under it. The negative margin is `ScreenTable`'s, for the same reason —
      // a rule reads as a band's edge only if it runs the full width of the
      // field.
      className="-mx-4 border-b border-border px-4 pb-4 lg:-mx-7 lg:px-7"
    >
      <div className="max-w-xl">
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
            <FormField
              control={form.control}
              name="number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Receipt number
                  </FormLabel>

                  {/* The button is **inside the field**, on the input's own row,
                      and the description sets under both. It used to be the
                      form's second flex child, bottom-aligned against a
                      description that was one line because the input was the
                      width of the card; on a measure that description is four
                      lines and the button floated level with the middle of it.
                      `FormControl` still wraps the input, so the id, the
                      description and the message are still wired by the
                      component. */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <FormControl
                      render={
                        <Input
                          // A number read aloud off paper and typed back in, so
                          // it gets the mono face and tabular figures — the same
                          // treatment the column in the list gives it, so the
                          // two look like the same fact.
                          className={`${fieldSurface} h-11 font-mono numeric sm:flex-1`}
                          placeholder={RECEIPT_NUMBER_EXAMPLE}
                          // Nothing here is a name, an address or a password,
                          // and a browser offering last week's numbers over the
                          // field is in the way of somebody reading this one off
                          // paper.
                          autoComplete="off"
                          spellCheck={false}
                          required
                          {...field}
                        />
                      }
                    />

                    {/* The screen's one accent. Nothing else on the sales list
                        or on a sale's record writes or navigates, so there is no
                        second control this could be confused with — and this is
                        the one somebody with a customer at the counter is
                        looking for. */}
                    <Button type="submit" className="h-11 sm:shrink-0" disabled={pending}>
                      <IconSearch data-icon="inline-start" />
                      {pending ? 'Looking…' : 'Find'}
                    </Button>
                  </div>

                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {/* `polite`: the answer arrives beside a field somebody has just used and is
          announced without cutting across what they are doing.

          The region is **always in the DOM**, empty while there is nothing to
          say. A live region that mounts at the same moment as its content is not
          reliably announced — the browser has nothing to compare against — and
          the first answer is the one somebody is waiting for. */}
        <div
          aria-live="polite"
          className={answer.kind === 'idle' ? undefined : 'mt-5 border-t border-border pt-4'}
        >
          <AnswerBody answer={answer} />
        </div>
      </div>
    </section>
  );
}

function AnswerBody({ answer }: { answer: Answer }) {
  switch (answer.kind) {
    case 'idle':
      return null;

    case 'looking':
      return <p className="text-sm text-muted-foreground">Looking…</p>;

    case 'here':
      return (
        <div className="flex items-start gap-2.5">
          {/* The plain receipt and not the struck-through one beside it: this is
              the found case, and it is the same artefact without the negation. */}
          <IconReceipt className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            <span className="font-mono text-foreground numeric">{answer.number}</span> is the sale
            below — you are already looking at it.
          </p>
        </div>
      );

    case 'several':
      return <Candidates answer={answer} />;

    case 'missing':
      return (
        <div className="flex items-start gap-2.5">
          {/* Not `destructive` and not a warning triangle. Nothing went wrong: the
              question was asked and the answer was no. Red in this system means
              the system refused something or is about to. */}
          <IconReceiptOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No receipt with that number</p>
            <p className="text-sm text-muted-foreground">
              Nothing carries{' '}
              <span className="font-mono text-foreground numeric">{answer.number}</span>.{' '}
              {/* The only thing this sentence varies on is whether a prefix was
                  typed, which is a fact about the input. It is **not** a branch
                  on why the server answered nothing: a receipt at an outlet
                  outside this caller's reach and one nobody has printed arrive
                  here as the same `404` and leave here as the same sentence. */}
              {answer.hadPrefix
                ? 'Check the number on the receipt.'
                : 'Check the digits, or add the letters from the top of the receipt.'}
            </p>
            {/* The misreading this exists to prevent. On the one screen where
                somebody is asking about a charge that may have been given back,
                an empty answer invites "so it must have been refunded" — and a
                refunded sale answers here, marked. */}
            <p className="text-sm text-muted-foreground">Refunded sales are still found.</p>
          </div>
        </div>
      );

    case 'failed':
      return (
        <div className="flex items-start gap-2.5">
          <IconAlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">That lookup did not answer</p>
            {/* The code becomes words in one place for the whole app. A screen
                that worded this itself would re-separate refusals the API merges
                on purpose. */}
            <p className="text-sm text-muted-foreground">{messageForFailure(answer.error)}</p>
          </div>
        </div>
      );
  }
}

/**
 * More than one till has printed that number, so the person chooses.
 *
 * Each row leads with the **full `receiptNumber` the server sent**, which is the
 * one thing that discriminates: it carries the till's prefix (FR-410), and that
 * prefix is printed on the top line of the paper in somebody's hand. Everything
 * beside it — when it was rung up, what it came to, how it was paid — is there
 * because a customer can usually answer one of those even when the top of the
 * receipt is gone.
 *
 * **The outlet is not named here and that is not an omission to fix carelessly.**
 * It would cost one `GET /v1/outlets/{id}` per candidate on the path where
 * somebody is standing at a counter waiting, and the prefix already names the
 * *device*, which is narrower than the outlet and is the thing actually printed.
 * The record each row leads to names the outlet in full.
 */
function Candidates({ answer }: { answer: Extract<Answer, { kind: 'several' }> }) {
  const { number, sales, total } = answer;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-medium text-foreground">
          {sales.length} sales carry{' '}
          <span className="font-mono text-foreground numeric">{number}</span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Match the letters against the top of the receipt.
        </p>

        {/* Documented not to happen: the route's own description says the page is
            complete, because the answer is bounded by the tills in the park
            rather than by a day's trading. Said out loud if it ever is not,
            because a list silently missing a candidate on this screen is how
            somebody concludes a receipt is not ours. */}
        {total > sales.length && (
          <p className="mt-1 text-sm text-muted-foreground">
            Showing {sales.length} of {total}.
          </p>
        )}
      </div>

      <ul className="space-y-2">
        {sales.map((sale) => (
          <li key={sale.id}>
            <CandidateLink sale={sale} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One candidate, as a whole-row target rather than a link inside a row of text. */
function CandidateLink({ sale }: { sale: SaleResource }) {
  const refunded = sale.refundedAt !== null;

  return (
    <Link
      to={salePath(sale.id)}
      // The number and the amount, because that pair is what somebody reads back
      // to the customer to confirm they have the right one before opening it.
      aria-label={`The sale numbered ${sale.receiptNumber} for ${formatPaise(sale.totalPaise)}`}
      className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-3xl border border-border px-4 py-3 outline-none hover:border-foreground/30 hover:bg-muted/40 focus-visible:ring-3 focus-visible:ring-ring/30"
    >
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-foreground numeric">{sale.receiptNumber}</span>

        {/* `outline` and not `destructive`, matching the list and the record. Red
            in this system means the system refused something or is about to; a
            refund is an ordinary correction that already happened. */}
        {refunded && <Badge variant="outline">Refunded</Badge>}
      </span>

      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-muted-foreground">
        {/* The till's clock — when the customer paid, which is the one they can
            answer about. */}
        <span>{formatInstant(sale.occurredAt)}</span>
        <span>{labelForPaymentMode(sale.paymentMode)}</span>
        <span
          className={`font-mono text-foreground numeric ${refunded ? 'text-muted-foreground line-through' : ''}`}
        >
          {formatPaise(sale.totalPaise)}
        </span>
      </span>
    </Link>
  );
}
